'use client'

import { useRouter } from 'next/navigation'
import { useEffect } from 'react'

/**
 * LegacyHashRedirect
 *
 * Client-side component that handles redirects from legacy hash-based URLs to modern routes.
 * This component is necessary because hash fragments (e.g., #/leagues) are only available
 * client-side and are never sent to the server.
 *
 * @component
 * @returns {null} This component renders nothing, it only performs side effects
 *
 * @example
 * // Old URL format:
 * // https://gridfinder.com/#/leagues?platform=PC&simulator=assetto-corsa
 * //
 * // Redirects to:
 * // /sim-racing-events?platforms=pc&game=assetto-corsa&days=Any&type=All+Race+Types&sort=All+Active&carClasses=Any&ts=<timestamp>&page=1
 *
 * @remarks
 * Parameter mapping:
 * - platform (e.g., "PC") → platforms (e.g., "pc")
 * - simulator (e.g., "assetto-corsa") → game (e.g., "assetto-corsa")
 *
 * Default parameters added:
 * - days=Any
 * - type=All+Race+Types
 * - sort=All+Active
 * - carClasses=Any
 * - ts=<current_timestamp>
 * - page=1
 */
export default function LegacyHashRedirect() {
  const router = useRouter()

  useEffect(() => {
    // Check if there's a hash in the URL
    if (typeof window === 'undefined' || !window.location.hash) {
      return
    }

    const hash = window.location.hash

    // Check if this is a legacy /leagues route
    if (hash.startsWith('#/leagues')) {
      try {
        // Extract query parameters from the hash
        const hashParts = hash.split('?')
        if (hashParts.length < 2) {
          // No query params, just redirect to events page with defaults
          const now = new Date().getTime()
          router.push(
            `/sim-racing-events?platforms=Any&game=assetto-corsa&days=Any&type=All+Race+Types&sort=All+Active&carClasses=Any&ts=${now}&page=1`,
          )
          return
        }

        // Parse query parameters
        const queryString = hashParts[1]
        const params = new URLSearchParams(queryString)

        // Map old parameters to new ones
        const platform = params.get('platform')?.toLowerCase() || 'Any'
        const simulator = params.get('simulator') || 'assetto-corsa'

        // Build new URL with mapped parameters
        const now = new Date().getTime()
        const newUrl = `/sim-racing-events?platforms=${platform}&game=${simulator}&days=Any&type=All+Race+Types&sort=All+Active&carClasses=Any&ts=${now}&page=1`

        // Perform the redirect
        router.push(newUrl)
      } catch (error) {
        console.error('Error parsing legacy hash URL:', error)
        // On error, just redirect to events page with defaults
        const now = new Date().getTime()
        router.push(
          `/sim-racing-events?platforms=Any&game=assetto-corsa&days=Any&type=All+Race+Types&sort=All+Active&carClasses=Any&ts=${now}&page=1`,
        )
      }
    }
  }, [router])

  // This component doesn't render anything
  return null
}
